import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { AdminAiAssistantWidget } from "@/components/assistant/AdminAiAssistantWidget";
import { AdminIssueReportWidget } from "@/components/assistant/AdminIssueReportWidget";
import { isAdminV2FoundationsEnabled } from "@/utils/featureFlags";
import { ShellSidePanel } from "@/vnext/app-shell/ShellSidePanel";

export type ShellPanelId = "log_issue" | "assistant";

export const SHELL_PANEL_LABELS: Record<ShellPanelId, string> = {
  log_issue: "Log issue",
  assistant: "AI Assistant",
};

/**
 * Which top bar tools show and which side panel is open (Nav PRD §7, §8).
 * The gates are the ones the retired floating buttons had: a signed-in
 * admin, not screenshot mode, and Admin V2 foundations for the assistant.
 */
export function useShellToolPanels(args: {
  currentUserId: string | null | undefined;
  screenshotMode: boolean;
}) {
  const signedIn = Boolean(args.currentUserId) && !args.screenshotMode;
  const available: Record<ShellPanelId, boolean> = {
    log_issue: signedIn,
    assistant: signedIn && isAdminV2FoundationsEnabled(),
  };
  const [active, setActive] = useState<ShellPanelId | null>(null);
  const openDrawers = useRef(0);
  const buttonRefs = useRef<Record<ShellPanelId, HTMLButtonElement | null>>({
    log_issue: null,
    assistant: null,
  });
  const [returnFocusTo, setReturnFocusTo] = useState<ShellPanelId | null>(null);

  const close = useCallback(() => {
    if (active) setReturnFocusTo(active);
    setActive(null);
  }, [active]);
  const toggle = useCallback(
    (id: ShellPanelId) => {
      if (openDrawers.current > 0) return;
      if (active === id) {
        close();
        return;
      }
      setActive(id);
    },
    [active, close],
  );

  // Closing a panel puts focus back on the button that opened it (F-214).
  useEffect(() => {
    if (!returnFocusTo) return;
    buttonRefs.current[returnFocusTo]?.focus();
    setReturnFocusTo(null);
  }, [returnFocusTo]);

  // A drawer takes focus itself; do not move it back to the panel trigger.
  const dismissForDrawer = useCallback(() => {
    openDrawers.current += 1;
    setActive(null);
    return () => {
      openDrawers.current -= 1;
    };
  }, []);
  const openId = active && available[active] ? active : null;
  return {
    active: openId,
    available,
    buttonRefs,
    close,
    dismissForDrawer,
    toggle,
  };
}

export function ShellToolPanels({
  active,
  available,
  currentUserId,
  onClose,
  panelId,
}: {
  active: ShellPanelId | null;
  available: Record<ShellPanelId, boolean>;
  currentUserId: string | undefined;
  onClose: () => void;
  panelId: string;
}) {
  // Retain local drafts while closed/switched, but never across staff identity
  // changes (the shell keys this host) or a revoked tool/screenshot gate.
  return (
    <>
      {available.log_issue ? (
        <ShellSidePanel
          data-hc-admin-issue-widget-root
          headerActions={
            <Link
              className="vnext-shell-panel__header-link"
              onClick={onClose}
              to="/issue-reports"
            >
              Open issue queue
            </Link>
          }
          id={panelId}
          onClose={onClose}
          open={active === "log_issue"}
          subtitle="Report a problem with the page you are on."
          title={SHELL_PANEL_LABELS.log_issue}
        >
          <AdminIssueReportWidget
            currentUserId={currentUserId}
            hosted={{ isOpen: active === "log_issue", onClose }}
          />
        </ShellSidePanel>
      ) : null}
      {available.assistant ? (
        <ShellSidePanel
          id={panelId}
          onClose={onClose}
          open={active === "assistant"}
          subtitle="Internal docs + code answers"
          title={SHELL_PANEL_LABELS.assistant}
        >
          <AdminAiAssistantWidget
            currentUserId={currentUserId}
            hosted={{ isOpen: active === "assistant", onClose }}
          />
        </ShellSidePanel>
      ) : null}
    </>
  );
}
